import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';

/* ════════════════════════════════════════════════════════════════
   TOVIS · Histórico Quarteirão
   Cabeçalho institucional escuro (padrão TOVIS) · conteúdo claro,
   alto contraste, TDAH-friendly. Ligado no motor /api/historico-quarteirao/dados.
   ════════════════════════════════════════════════════════════════ */

const API_BASE = '/api/historico-quarteirao';

// ─── Tipos do pacote de dados que o motor devolve ──────────────────
type AreaInfo = {
  id: number; nome: string; zona?: string;
  total_quarteiroes: number; total_imoveis: number; em_campo?: boolean;
};
type CicloInfo = { id: number; numero: number; ano: number; texto: string };
type Progresso = { trabalhados: number; total?: number; percentual?: number; faltam: number; excedente?: number };
type ImoveisInfo = { trabalhados: number; total?: number; percentual?: number; faltam: number };
type QuarteiraoTrab = {
  quarteirao?: string; numero?: string; agente?: string; data?: string;
  se?: number | string; fad01_id?: number | string;
  imoveis_trabalhados?: number; imoveis_inspecionados?: number; imoveis?: number;
};
type PorAgente = { agente?: string; nome?: string; quarteiroes?: number; total?: number };
type Lancamento = {
  quarteirao?: string; numero?: string; agente?: string; nome?: string;
  data?: string; hora?: string;
};
type Filtro = { id: number | string; texto?: string; nome?: string; numero?: number };
type Dados = {
  filtros_ativos?: { area_id?: number; ciclo_id?: number; se?: number | string; agente_id?: number; data_inicio?: string; data_fim?: string };
  area?: AreaInfo;
  ciclo?: CicloInfo;
  progresso?: Progresso;
  imoveis?: ImoveisInfo;
  quarteiroes_trabalhados?: QuarteiraoTrab[];
  por_agente?: PorAgente[];
  ultimos_lancamentos?: Lancamento[];
  total_lancamentos?: number;
  // listas pros seletores (se o motor mandar; senão derivamos)
  areas?: Filtro[];
  ciclos?: Filtro[];
  agentes?: Filtro[];
  ses?: (number | string)[];
};

// ─── Helpers ───────────────────────────────────────────────────────
const iniciais = (nome?: unknown): string => {
  let txt = '';
  if (typeof nome === 'string') txt = nome;
  else if (typeof nome === 'number') txt = String(nome);
  else if (nome && typeof nome === 'object') txt = String((nome as any).nome ?? (nome as any).agente ?? '');
  txt = txt.trim();
  if (!txt) return '?';
  return txt.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('') || '?';
};

const numQ = (q: QuarteiraoTrab) => String(q.quarteirao ?? q.numero ?? '?');
const nomeTexto = (v?: unknown): string => {
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  if (v && typeof v === 'object') return String((v as any).nome ?? (v as any).agente ?? '—');
  return '—';
};
const agQ = (q: QuarteiraoTrab) => nomeTexto(q.agente);
const pct = (parte?: number, tot?: number) =>
  !tot || tot <= 0 ? 0 : Math.round((1000 * (parte || 0)) / tot) / 10;

const fmtData = (d?: string) => {
  if (!d) return '—';
  // aceita ISO ou já-formatado
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return d;
};
const diaMes = (d?: string) => {
  const f = fmtData(d);
  const p = f.split('/');
  return p.length >= 2 ? `${p[0]}/${p[1]}` : f;
};

// ─── CSS (escopado num <style> dentro do componente) ───────────────
const CSS = `
.hq-scope *, .hq-scope *::before, .hq-scope *::after { box-sizing: border-box; }
.hq-scope {
  --d-bg:#030b16; --d-bg-mid:#060f1e; --d-bg-card:#0a1828; --d-bord:rgba(255,255,255,0.08);
  --d-white:#f0f4f8; --d-gray-lt:#b0bcc8; --d-gray:#8aa0b8;
  --canvas:#eef2f7; --paper:#ffffff; --paper-tint:#f8fafc; --ink:#1a2532; --ink-soft:#2a3441;
  --muted:#5a6470; --muted-2:#8a929c; --muted-3:#b6bcc4; --rule:#e2e7ee; --rule-soft:#eef1f5; --row-band:#f5f7fa;
  --cyan:#00b8cc; --cyan-d:#0099b0; --cyan-soft:#e4f5f7; --cyan-tint:#b8e7ed;
  --orange:#e85d26; --orange-soft:#fceee7; --amber:#d97706; --amber-soft:#fdf3dd;
  --green:#16a34a; --green-soft:#e1f5e8; --red:#dc2626; --red-soft:#fde7e7; --purple:#7c3aed;
  --mono:'JetBrains Mono','Courier New',monospace;
  font-family:'Inter',system-ui,-apple-system,sans-serif;
  background:var(--canvas); color:var(--ink); font-size:13.5px; line-height:1.55;
  -webkit-font-smoothing:antialiased; min-height:100%;
}
.hq-scope .app { max-width:1380px; margin:0 auto; padding:0 0 60px; }

/* PAGE HEAD escuro */
.hq-scope .page-head { position:relative; background:
  radial-gradient(ellipse 80% 80% at 90% 50%, rgba(0,184,204,0.06) 0%, transparent 60%),
  linear-gradient(180deg,#061021 0%,#050d1c 100%);
  padding:22px 32px 24px; color:var(--d-white); border-bottom:1px solid rgba(0,184,204,0.16); overflow:hidden; }
.hq-scope .page-head::before { content:''; position:absolute; top:0; left:0; right:0; height:2px;
  background:linear-gradient(90deg,transparent 0%,var(--cyan) 30%,var(--orange) 60%,var(--cyan) 80%,transparent 100%); opacity:0.6; }
.hq-scope .page-head::after { content:''; position:absolute; inset:0;
  background-image:linear-gradient(rgba(0,184,204,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(0,184,204,0.05) 1px,transparent 1px);
  background-size:48px 48px; pointer-events:none;
  -webkit-mask-image:radial-gradient(ellipse 60% 100% at 80% 50%,#000 30%,transparent 80%);
  mask-image:radial-gradient(ellipse 60% 100% at 80% 50%,#000 30%,transparent 80%); }
.hq-scope .ph-row { position:relative; z-index:1; display:flex; align-items:center; gap:18px; flex-wrap:wrap; }
.hq-scope .ph-back { display:inline-flex; align-items:center; gap:7px; padding:7px 14px 7px 11px;
  border:1px solid rgba(255,255,255,0.18); background:rgba(255,255,255,0.03); border-radius:8px;
  color:var(--d-white); font-size:12.5px; font-weight:600; cursor:pointer; text-decoration:none; transition:all .15s; }
.hq-scope .ph-back:hover { background:rgba(0,184,204,0.12); border-color:rgba(0,184,204,0.4); color:var(--cyan); }
.hq-scope .ph-title-col { flex:1; display:flex; flex-direction:column; gap:6px; min-width:240px; }
.hq-scope .ph-title-row { display:flex; align-items:center; gap:12px; }
.hq-scope .ph-icon { color:var(--cyan); flex-shrink:0; }
.hq-scope .ph-title { font-size:22px; font-weight:800; color:var(--d-white); letter-spacing:-0.018em; line-height:1; }
.hq-scope .ph-status-dot { width:8px; height:8px; border-radius:50%; background:var(--cyan); box-shadow:0 0 8px var(--cyan); }
.hq-scope .ph-underline { height:2px; width:54px; background:linear-gradient(90deg,var(--cyan),transparent); border-radius:2px; }
.hq-scope .ph-sub { font-size:12px; color:var(--d-gray); }
.hq-scope .ph-sub .pip { color:rgba(255,255,255,0.2); margin:0 6px; }
.hq-scope .ph-sub .em { color:var(--d-gray-lt); }
.hq-scope .ph-metrics { display:flex; gap:10px; }
.hq-scope .ph-chip { padding:8px 14px; border:1px solid var(--d-bord); border-radius:9px; background:rgba(255,255,255,0.02); text-align:center; min-width:60px; }
.hq-scope .ph-chip .l { font-family:var(--mono); font-size:9px; letter-spacing:0.12em; text-transform:uppercase; color:var(--d-gray); margin-bottom:3px; }
.hq-scope .ph-chip .v { font-family:var(--mono); font-size:15px; font-weight:700; color:var(--d-white); }
.hq-scope .ph-chip.is-cyan { border-color:rgba(0,184,204,0.4); background:rgba(0,184,204,0.1); }
.hq-scope .ph-chip.is-cyan .v { color:var(--cyan); }

/* CONTENT claro */
.hq-scope .content { padding:26px 32px 0; }
.hq-scope .filters { display:flex; align-items:center; gap:14px; padding:12px 16px; background:var(--paper);
  border:1px solid var(--rule); border-radius:10px; margin-bottom:8px; box-shadow:0 1px 2px rgba(15,20,30,0.03); flex-wrap:wrap; }
.hq-scope .filter { display:flex; flex-direction:column; gap:3px; padding-right:14px; border-right:1px solid var(--rule-soft); }
.hq-scope .filter:last-of-type { border-right:none; padding-right:0; }
.hq-scope .filter .l { font-family:var(--mono); font-size:9.5px; font-weight:600; letter-spacing:0.14em; text-transform:uppercase; color:var(--muted-2); }
.hq-scope .filter select { appearance:none; background:transparent; border:none; font-family:'Inter',sans-serif; font-size:13.5px; font-weight:600; color:var(--ink); padding:0 22px 0 0; cursor:pointer; outline:none;
  background-image:url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' stroke='%235a6470' stroke-width='1.4' fill='none' stroke-linecap='round'/></svg>");
  background-repeat:no-repeat; background-position:right 4px center; min-width:130px; }
.hq-scope .filter select:hover { color:var(--cyan-d); }
.hq-scope .filter input[type=date] { appearance:none; background:transparent; border:none; font-family:'Inter',sans-serif; font-size:13.5px; font-weight:600; color:var(--ink); outline:none; cursor:pointer; min-width:130px; }
.hq-scope .filter input[type=date]::-webkit-calendar-picker-indicator { opacity:0.4; cursor:pointer; }
.hq-scope .filters .spacer { flex:1; }
.hq-scope .actions { display:flex; gap:8px; }
.hq-scope .btn { display:inline-flex; align-items:center; gap:6px; padding:8px 14px; border-radius:7px; background:var(--paper);
  border:1px solid var(--rule); color:var(--ink-soft); font-family:'Inter',sans-serif; font-size:12.5px; font-weight:600; cursor:pointer; transition:all .15s; }
.hq-scope .btn:hover { border-color:var(--cyan); color:var(--cyan-d); background:var(--cyan-soft); }
.hq-scope .btn.is-primary { border-color:var(--cyan); color:var(--cyan-d); background:var(--cyan-soft); }
.hq-scope .btn.is-primary:hover { background:var(--cyan-tint); }
.hq-scope .btn:disabled { opacity:0.5; cursor:default; }

.hq-scope .caption { display:flex; align-items:center; gap:10px; padding:9px 16px; background:var(--cyan-soft);
  border:1px solid var(--cyan-tint); border-top:none; border-radius:0 0 10px 10px; font-size:12px; color:var(--ink-soft); margin-bottom:26px; flex-wrap:wrap; }
.hq-scope .caption .l { font-family:var(--mono); font-weight:700; letter-spacing:0.16em; text-transform:uppercase; font-size:9.5px; color:var(--cyan-d); }
.hq-scope .caption strong { color:var(--ink); font-weight:700; }
.hq-scope .caption .pip { color:var(--muted-3); }
.hq-scope .caption .right { margin-left:auto; font-family:var(--mono); color:var(--muted); font-size:11px; }
.hq-scope .caption .right strong { color:var(--cyan-d); font-weight:700; }

/* HERO */
.hq-scope .hero { position:relative; background:var(--paper); border:1px solid var(--rule); border-radius:14px; padding:22px 26px; margin-bottom:18px; overflow:hidden; box-shadow:0 2px 8px rgba(15,20,30,0.04); }
.hq-scope .hero::before { content:''; position:absolute; top:0; left:0; bottom:0; width:4px; background:linear-gradient(180deg,var(--cyan),var(--cyan-d)); }
.hq-scope .hero-top { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:18px; flex-wrap:wrap; gap:8px; }
.hq-scope .hero-kicker { position:relative; padding-left:16px; font-family:var(--mono); font-size:10.5px; font-weight:700; letter-spacing:0.16em; text-transform:uppercase; color:var(--cyan-d); }
.hq-scope .hero-kicker::before { content:''; position:absolute; left:0; top:50%; transform:translateY(-50%); width:8px; height:8px; border-radius:2px; background:var(--cyan); }
.hq-scope .hero-area { font-family:var(--mono); font-size:11px; letter-spacing:0.06em; color:var(--muted); }
.hq-scope .hero-area strong { color:var(--ink); font-weight:700; }
.hq-scope .hero-area .em { color:var(--cyan-d); }
.hq-scope .hero-row { display:flex; align-items:center; gap:28px; }
.hq-scope .hero-pct { font-family:var(--mono); font-size:64px; font-weight:800; color:var(--ink); line-height:0.9; letter-spacing:-0.03em; font-variant-numeric:tabular-nums; }
.hq-scope .hero-pct .u { font-size:30px; color:var(--cyan-d); font-weight:700; }
.hq-scope .hero-bar-col { flex:1; display:flex; flex-direction:column; gap:12px; }
.hq-scope .hero-title { font-size:14px; color:var(--ink-soft); font-weight:500; }
.hq-scope .hero-title .em { color:var(--cyan-d); font-weight:700; }
.hq-scope .hero-title .dim { color:var(--muted); }
.hq-scope .hero-bar { position:relative; height:22px; background:var(--row-band); border-radius:6px; overflow:hidden; border:1px solid var(--rule); }
.hq-scope .hero-bar .fill { height:100%; background:linear-gradient(90deg,var(--cyan-d),var(--cyan)); border-radius:6px 0 0 6px; position:relative; transition:width .5s ease; }
.hq-scope .hero-bar .ticks { position:absolute; inset:0; display:flex; justify-content:space-between; pointer-events:none; }
.hq-scope .hero-bar .ticks span { width:1px; background:rgba(0,0,0,0.04); }
.hq-scope .hero-scale { display:flex; justify-content:space-between; font-family:var(--mono); font-size:9.5px; color:var(--muted-3); }

/* KPIs */
.hq-scope .kpis { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:26px; }
.hq-scope .kpi { position:relative; background:var(--paper); border:1px solid var(--rule); border-radius:12px; padding:18px 20px; overflow:hidden; box-shadow:0 2px 6px rgba(15,20,30,0.04); }
.hq-scope .kpi::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:var(--cyan); }
.hq-scope .kpi.is-ochre::before { background:var(--orange); }
.hq-scope .kpi .head { display:flex; align-items:center; gap:9px; margin-bottom:14px; }
.hq-scope .kpi .icon { width:30px; height:30px; border-radius:8px; background:var(--cyan-soft); color:var(--cyan-d); display:flex; align-items:center; justify-content:center; }
.hq-scope .kpi.is-ochre .icon { background:var(--orange-soft); color:var(--orange); }
.hq-scope .kpi .head .l { font-family:var(--mono); font-size:10px; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; color:var(--cyan-d); }
.hq-scope .kpi.is-ochre .head .l { color:var(--orange); }
.hq-scope .kpi-body { display:flex; gap:20px; align-items:flex-start; }
.hq-scope .kpi-num { font-family:var(--mono); font-size:38px; font-weight:800; color:var(--ink); line-height:1; letter-spacing:-0.02em; font-variant-numeric:tabular-nums; white-space:nowrap; }
.hq-scope .kpi-num .of { color:var(--muted-3); margin:0 2px; font-weight:400; }
.hq-scope .kpi-num .total { color:var(--muted-2); font-size:24px; }
.hq-scope .kpi-side { flex:1; display:flex; flex-direction:column; gap:8px; }
.hq-scope .kpi-side .row { font-size:12px; color:var(--muted); }
.hq-scope .kpi-side .row .v { color:var(--ink-soft); font-weight:600; }
.hq-scope .kpi-mini { height:6px; background:var(--row-band); border-radius:3px; overflow:hidden; }
.hq-scope .kpi-mini > span { display:block; height:100%; background:var(--cyan); border-radius:3px; }
.hq-scope .kpi.is-ochre .kpi-mini > span { background:var(--orange); }
.hq-scope .kpi-meta { display:flex; justify-content:space-between; font-family:var(--mono); font-size:10px; color:var(--muted-2); }

/* SECTION LABEL */
.hq-scope .section-label { display:flex; align-items:center; gap:12px; margin-bottom:14px; }
.hq-scope .section-label .bar { width:4px; height:18px; background:var(--cyan); border-radius:2px; }
.hq-scope .section-label .label { font-size:15px; font-weight:700; color:var(--ink); letter-spacing:-0.01em; }
.hq-scope .section-label .aside { margin-left:auto; font-family:var(--mono); font-size:11px; color:var(--muted); }
.hq-scope .section-label .aside strong { color:var(--cyan-d); font-weight:700; }

/* MAP LAYOUT */
.hq-scope .map-layout { display:grid; grid-template-columns:1fr 320px; gap:18px; }
.hq-scope .map { background:var(--paper); border:1px solid var(--rule); border-radius:12px; padding:18px; box-shadow:0 2px 6px rgba(15,20,30,0.04); }
.hq-scope .map-head { display:flex; justify-content:space-between; align-items:center; margin-bottom:14px; flex-wrap:wrap; gap:8px; }
.hq-scope .map-head .title { font-size:14px; font-weight:700; color:var(--ink); display:flex; align-items:center; gap:10px; }
.hq-scope .map-head .title .area-pill { font-family:var(--mono); font-size:10px; font-weight:700; letter-spacing:0.08em; color:var(--cyan-d); background:var(--cyan-soft); border:1px solid var(--cyan-tint); padding:2px 8px; border-radius:5px; }
.hq-scope .legend { display:flex; align-items:center; gap:14px; font-size:11px; color:var(--muted); }
.hq-scope .legend .item { display:inline-flex; align-items:center; gap:6px; }
.hq-scope .legend .chip { width:13px; height:13px; border-radius:4px; }
.hq-scope .legend .chip.is-done { background:linear-gradient(160deg,#e8f7f9,#c4e8ed); border:1.5px solid var(--cyan); }
.hq-scope .legend .chip.is-todo { background:repeating-linear-gradient(45deg,#f0f3f7 0 4px,#fafbfd 4px 8px); border:1.5px dashed var(--muted-3); }
.hq-scope .legend .hint { font-family:var(--mono); font-size:9.5px; color:var(--muted-3); }

.hq-scope .grid-wrap { position:relative; padding:16px; background:linear-gradient(rgba(0,184,204,0.05) 1px,transparent 1px),linear-gradient(90deg,rgba(0,184,204,0.05) 1px,transparent 1px),var(--paper-tint); background-size:24px 24px; border:1px solid var(--rule); border-radius:10px; }
.hq-scope .grid { display:grid; grid-template-columns:repeat(11,1fr); gap:9px; position:relative; }
.hq-scope .cell { aspect-ratio:1/1; position:relative; display:flex; align-items:center; justify-content:center; font-family:var(--mono); font-size:17px; font-weight:700; border-radius:7px; user-select:none; transition:transform .15s, box-shadow .15s, border-color .15s, background .15s; }
.hq-scope .cell.is-done { background:linear-gradient(160deg,#e8f7f9,#c4e8ed); border:1.5px solid var(--cyan); color:var(--cyan-d); cursor:pointer; box-shadow:inset 0 1px 0 rgba(255,255,255,0.6),0 1px 2px rgba(0,184,204,0.08); }
.hq-scope .cell.is-done::after { content:''; position:absolute; top:5px; right:5px; width:6px; height:6px; border-radius:50%; background:var(--cyan); box-shadow:0 0 4px rgba(0,184,204,0.5); }
.hq-scope .cell.is-done:hover { transform:translateY(-2px); background:linear-gradient(160deg,#d3eef2,#a5dde4); border-color:var(--cyan-d); box-shadow:0 8px 16px rgba(0,184,204,0.22),0 0 0 2px rgba(0,184,204,0.35); color:var(--ink); z-index:5; }
.hq-scope .cell.is-done.is-sub { font-size:13.5px; letter-spacing:-0.02em; }
.hq-scope .cell.is-todo { background:repeating-linear-gradient(45deg,#f0f3f7 0 4px,#fafbfd 4px 8px); border:1.5px dashed var(--muted-3); color:var(--muted-3); font-family:'Inter',sans-serif; font-size:18px; font-weight:300; }
.hq-scope .cell.is-todo::before { content:'?'; color:var(--muted-3); opacity:0.7; }

.hq-scope .map-foot { display:flex; justify-content:space-between; align-items:center; margin-top:16px; padding-top:14px; border-top:1px solid var(--rule); font-family:var(--mono); font-size:10.5px; color:var(--muted-2); letter-spacing:0.06em; text-transform:uppercase; flex-wrap:wrap; gap:10px; }
.hq-scope .map-foot .agents { display:flex; gap:16px; flex-wrap:wrap; }
.hq-scope .map-foot .agents .a { display:inline-flex; align-items:center; gap:7px; text-transform:none; letter-spacing:0; }
.hq-scope .map-foot .agents .a .av { width:20px; height:20px; border-radius:50%; background:linear-gradient(135deg,#1a3260,var(--cyan)); border:1px solid var(--cyan-d); color:#fff; display:inline-flex; align-items:center; justify-content:center; font-size:9.5px; font-weight:700; font-family:'Inter',sans-serif; }
.hq-scope .map-foot .agents .a .name { color:var(--ink-soft); font-family:'Inter',sans-serif; font-size:11.5px; font-weight:500; }
.hq-scope .map-foot .agents .a .ct { color:var(--cyan-d); font-family:var(--mono); font-size:11.5px; font-weight:700; padding:1px 6px; background:var(--cyan-soft); border-radius:4px; }

/* FLOAT POPOVER */
.hq-scope .float-pop { position:fixed; z-index:1000; pointer-events:none; opacity:0; transform:translateY(-4px); transition:opacity .12s, transform .12s;
  background:var(--paper); border:1px solid var(--cyan); border-radius:10px; padding:14px 16px; min-width:250px; box-shadow:0 18px 40px rgba(15,20,30,0.18),0 4px 10px rgba(0,184,204,0.18); }
.hq-scope .float-pop.is-visible { opacity:1; transform:translateY(0); }
.hq-scope .float-pop::before { content:''; position:absolute; top:0; left:0; right:0; height:4px; background:var(--cyan); border-radius:10px 10px 0 0; }
.hq-scope .float-pop .pop-kicker { font-family:var(--mono); font-size:9.5px; font-weight:700; letter-spacing:0.18em; text-transform:uppercase; color:var(--cyan-d); margin:2px 0 3px; }
.hq-scope .float-pop .pop-num { font-size:30px; color:var(--ink); font-weight:800; line-height:1; letter-spacing:-0.02em; margin-bottom:12px; font-family:var(--mono); font-variant-numeric:tabular-nums; }
.hq-scope .float-pop .pop-row { display:grid; grid-template-columns:68px 1fr; gap:4px 12px; padding:6px 0; border-top:1px solid var(--rule-soft); font-size:12px; align-items:baseline; }
.hq-scope .float-pop .pop-row .l { font-family:var(--mono); font-size:9.5px; font-weight:700; letter-spacing:0.12em; text-transform:uppercase; color:var(--muted-2); }
.hq-scope .float-pop .pop-row .v { color:var(--ink); font-size:13px; font-weight:500; }
.hq-scope .float-pop .pop-row .v.mono { font-family:var(--mono); color:var(--cyan-d); font-size:11.5px; font-weight:600; }

/* FEED */
.hq-scope .feed { background:var(--paper); border:1px solid var(--rule); border-radius:12px; padding:20px 18px 10px; align-self:start; position:relative; box-shadow:0 2px 6px rgba(15,20,30,0.04); }
.hq-scope .feed::before { content:''; position:absolute; top:0; left:0; right:0; height:4px; background:linear-gradient(90deg,var(--cyan),var(--purple)); opacity:0.85; border-radius:12px 12px 0 0; }
.hq-scope .feed-head { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px; padding-bottom:10px; border-bottom:1px solid var(--rule); }
.hq-scope .feed-head .t { font-size:14px; font-weight:700; color:var(--ink); }
.hq-scope .feed-head .ct { font-family:var(--mono); font-size:10px; color:var(--muted); letter-spacing:0.08em; text-transform:uppercase; }
.hq-scope .feed-list { display:flex; flex-direction:column; }
.hq-scope .feed-item { display:grid; grid-template-columns:28px 1fr auto; gap:10px; padding:11px 0; border-bottom:1px solid var(--rule-soft); align-items:center; }
.hq-scope .feed-item:last-child { border-bottom:none; }
.hq-scope .feed-item .av { width:28px; height:28px; border-radius:50%; background:linear-gradient(135deg,#1a3260,var(--cyan)); border:1px solid var(--cyan-d); display:flex; align-items:center; justify-content:center; color:#fff; font-size:10.5px; font-weight:700; }
.hq-scope .feed-item.is-old .av { background:linear-gradient(135deg,#b6bcc4,#8a929c); border-color:var(--muted-2); }
.hq-scope .feed-item .body { display:flex; flex-direction:column; gap:2px; }
.hq-scope .feed-item .num { font-family:var(--mono); font-size:13px; font-weight:700; color:var(--ink); }
.hq-scope .feed-item .num .label { color:var(--muted); margin-right:4px; font-weight:500; }
.hq-scope .feed-item .agent { font-size:11.5px; color:var(--muted); }
.hq-scope .feed-item .when { font-family:var(--mono); font-size:10.5px; color:var(--muted); text-align:right; }
.hq-scope .feed-item .when .d { color:var(--cyan-d); display:block; font-weight:700; font-size:11px; }
.hq-scope .feed-foot { padding:12px 0 10px; text-align:center; border-top:1px solid var(--rule); margin-top:6px; }
.hq-scope .feed-foot a { font-family:var(--mono); font-size:10.5px; font-weight:700; letter-spacing:0.14em; text-transform:uppercase; color:var(--cyan-d); text-decoration:none; cursor:pointer; }

.hq-scope .doc-foot { margin-top:38px; padding-top:14px; border-top:1px solid var(--rule); display:flex; justify-content:space-between; font-family:var(--mono); font-size:10.5px; color:var(--muted-2); letter-spacing:0.06em; flex-wrap:wrap; gap:8px; }
.hq-scope .doc-foot .em { color:var(--cyan-d); }

.hq-scope .hq-state { padding:60px 20px; text-align:center; color:var(--muted); font-size:14px; }
.hq-scope .hq-state .spin { display:inline-block; width:26px; height:26px; border:3px solid var(--rule); border-top-color:var(--cyan); border-radius:50%; animation:hqspin 0.8s linear infinite; margin-bottom:14px; }
@keyframes hqspin { to { transform:rotate(360deg); } }

@media (max-width:1100px) {
  .hq-scope .map-layout { grid-template-columns:1fr; }
  .hq-scope .grid { grid-template-columns:repeat(8,1fr); }
  .hq-scope .kpis { grid-template-columns:1fr; }
  .hq-scope .ph-metrics { width:100%; justify-content:flex-end; }
}
@media (max-width:720px) {
  .hq-scope .grid { grid-template-columns:repeat(6,1fr); }
  .hq-scope .hero-row { flex-direction:column; align-items:flex-start; gap:14px; }
}
`;

// ─── Componente ─────────────────────────────────────────────────────
class HQErrorBoundary extends React.Component<{children: React.ReactNode}, {erro: boolean}> {
  constructor(props: any){ super(props); this.state = { erro: false }; }
  static getDerivedStateFromError(){ return { erro: true }; }
  componentDidCatch(e: any){ console.error('HQ erro capturado:', e); }
  render(){
    if (this.state.erro) {
      return React.createElement('div', { style: { padding: 40, fontFamily: 'sans-serif', color: '#555' } },
        'Não consegui montar esta tela com os dados atuais. Atualize a página; se persistir, avise o suporte.');
    }
    return this.props.children;
  }
}

export default function HistoricoQuarteiraoWrapper() {
  return React.createElement(HQErrorBoundary, null, React.createElement(HistoricoQuarteiraoInner));
}

function HistoricoQuarteiraoInner() {
  const { token, usuario } = useAuth();

  const [dados, setDados] = useState<Dados | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // filtros selecionados
  const [areaId, setAreaId] = useState<string>('');
  const [cicloId, setCicloId] = useState<string>('');
  const [se, setSe] = useState<string>('');
  const [agenteId, setAgenteId] = useState<string>('');
  const [dataInicio, setDataInicio] = useState<string>('');
  const [dataFim, setDataFim] = useState<string>('');

  const [relogio, setRelogio] = useState('');
  const popRef = useRef<HTMLDivElement | null>(null);

  // relógio da barra de topo
  useEffect(() => {
    const tick = () => {
      const n = new Date();
      const p = (x: number) => String(x).padStart(2, '0');
      setRelogio(`${p(n.getHours())}:${p(n.getMinutes())}:${p(n.getSeconds())}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const buscar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const qs = new URLSearchParams();
      if (areaId) qs.set('area_id', areaId);
      if (cicloId) qs.set('ciclo_id', cicloId);
      if (se) qs.set('se', se);
      if (agenteId) qs.set('agente_id', agenteId);
      if (dataInicio) qs.set('data_inicio', dataInicio);
      if (dataFim) qs.set('data_fim', dataFim);
      const url = `${API_BASE}/dados${qs.toString() ? `?${qs}` : ''}`;
      const resp = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const j: Dados = await resp.json();
      setDados(j);
      // sincroniza os seletores com o que o motor diz que está ativo
      if (j.filtros_ativos) {
        if (j.filtros_ativos.area_id != null && !areaId) setAreaId(String(j.filtros_ativos.area_id));
        if (j.filtros_ativos.ciclo_id != null && !cicloId) setCicloId(String(j.filtros_ativos.ciclo_id));
      }
    } catch (e: any) {
      setErro('Não consegui carregar os dados do Histórico Quarteirão agora.');
    } finally {
      setCarregando(false);
    }
  }, [areaId, cicloId, se, agenteId, dataInicio, dataFim, token]);

  useEffect(() => {
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId, cicloId, se, agenteId, dataInicio, dataFim]);

  // ─── Popover ao passar o mouse numa célula trabalhada ───
  const mostrarPop = (q: QuarteiraoTrab, e: React.MouseEvent) => {
    const pop = popRef.current;
    if (!pop) return;
    const set = (cls: string, txt: string) => {
      const el = pop.querySelector<HTMLElement>(cls);
      if (el) el.textContent = txt;
    };
    set('.fp-num', numQ(q));
    set('.fp-agent', agQ(q));
    set('.fp-date', fmtData(q.data));
    const it = q.imoveis_trabalhados ?? q.imoveis;
    const ii = q.imoveis_inspecionados;
    set('.fp-imov', it != null ? `${it} trab.${ii != null ? ` · ${ii} inspec.` : ''}` : '—');
    set('.fp-se', q.se != null ? `SE ${q.se}` : '—');
    const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
    pop.style.left = `${r.left + r.width / 2 - 24}px`;
    pop.style.top = `${r.bottom + 10}px`;
    pop.classList.add('is-visible');
  };
  const esconderPop = () => popRef.current?.classList.remove('is-visible');

  // ─── Derivações de exibição ───
  const area = dados?.area;
  const ciclo = dados?.ciclo;
  const prog = dados?.progresso;
  const imov = dados?.imoveis;
  const trabalhados = dados?.quarteiroes_trabalhados ?? [];
  const totalQ = area?.total_quarteiroes ?? prog?.total ?? trabalhados.length + (prog?.faltam ?? 0);
  const feitos = prog?.trabalhados ?? trabalhados.length;
  const faltam = prog?.faltam ?? Math.max(0, totalQ - feitos);
  const progPct = prog?.percentual ?? pct(feitos, totalQ);
  const imovTotal = area?.total_imoveis ?? imov?.total ?? 0;
  const imovFeitos = imov?.trabalhados ?? 0;
  const imovPct = imov?.percentual ?? pct(imovFeitos, imovTotal);
  const imovFaltam = imov?.faltam ?? Math.max(0, imovTotal - imovFeitos);
  const porAgente = dados?.por_agente ?? [];
  const ultimos = dados?.ultimos_lancamentos ?? [];
  const nomeArea = area?.nome ?? '—';
  const totalLanc = dados?.total_lancamentos ?? trabalhados.length;

  // listas dos seletores (usa as que o motor mandar; senão deriva do que tem)
  const listaAreas = dados?.areas ?? (area ? [{ id: area.id, nome: area.nome }] : []);
  const listaCiclos = dados?.ciclos ?? (ciclo ? [{ id: ciclo.id, texto: ciclo.texto }] : []);
  const listaAgentes: Filtro[] = (dados?.agentes ?? porAgente.map((a, i) => ({ id: i, nome: a.agente ?? a.nome }))) as Filtro[];
  const listaSes = dados?.ses ?? [];

  const ultData = ultimos[0]?.data ?? trabalhados[trabalhados.length - 1]?.data;

  return (
    <div className="hq-scope">
      <style>{CSS}</style>
      <div className="app">

        {/* ─── Cabeçalho escuro ─── */}
        <div className="page-head">
          <div className="ph-row">
            <a className="ph-back" onClick={() => window.history.back()}>
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
                <path d="M9 11L5 7l4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Voltar
            </a>
            <div className="ph-title-col">
              <div className="ph-title-row">
                <span className="ph-icon">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <rect x="2.5" y="2.5" width="7" height="7" rx="1.2" stroke="currentColor" strokeWidth="1.7" />
                    <rect x="12.5" y="2.5" width="7" height="7" rx="1.2" stroke="currentColor" strokeWidth="1.7" fill="currentColor" fillOpacity="0.28" />
                    <rect x="2.5" y="12.5" width="7" height="7" rx="1.2" stroke="currentColor" strokeWidth="1.7" fill="currentColor" fillOpacity="0.28" />
                    <rect x="12.5" y="12.5" width="7" height="7" rx="1.2" stroke="currentColor" strokeWidth="1.7" />
                  </svg>
                </span>
                <span className="ph-title">Histórico Quarteirão</span>
                <span className="ph-status-dot" title="em campo" />
              </div>
              <div className="ph-underline" />
              <div className="ph-sub">
                Cobertura territorial em tempo real
                <span className="pip">·</span>
                <span className="em">Alimentado pelos lançamentos do FAD-01</span>
              </div>
            </div>
            <div className="ph-metrics">
              <div className="ph-chip is-cyan"><div className="l">Trab.</div><div className="v">{feitos}/{totalQ}</div></div>
              <div className="ph-chip"><div className="l">Faltam</div><div className="v">{faltam}</div></div>
              <div className="ph-chip"><div className="l">SE</div><div className="v">{se || (typeof dados?.filtros_ativos?.se !== 'undefined' ? dados?.filtros_ativos?.se : '—')}</div></div>
              <div className="ph-chip"><div className="l">Últ.</div><div className="v">{diaMes(ultData)}</div></div>
            </div>
          </div>
        </div>

        {/* ─── Conteúdo claro ─── */}
        <div className="content">

          {/* Filtros */}
          <div className="filters">
            <div className="filter">
              <span className="l">Área</span>
              <select value={areaId} onChange={(e) => setAreaId(e.target.value)}>
                {!areaId && <option value="">Área em campo</option>}
                {listaAreas.map((a) => (
                  <option key={String(a.id)} value={String(a.id)}>{nomeTexto(a.nome)}</option>
                ))}
              </select>
            </div>
            <div className="filter">
              <span className="l">Ciclo</span>
              <select value={cicloId} onChange={(e) => setCicloId(e.target.value)}>
                {!cicloId && <option value="">Ciclo ativo</option>}
                {listaCiclos.map((c) => (
                  <option key={String(c.id)} value={String(c.id)}>{c.texto ?? `${c.numero}`}</option>
                ))}
              </select>
            </div>
            <div className="filter">
              <span className="l">SE</span>
              <select value={se} onChange={(e) => setSe(e.target.value)}>
                <option value="">Todas do ciclo</option>
                {listaSes.map((s) => (
                  <option key={String(s)} value={String(s)}>SE {s}</option>
                ))}
              </select>
            </div>
            <div className="filter">
              <span className="l">Agente</span>
              <select value={agenteId} onChange={(e) => setAgenteId(e.target.value)}>
                <option value="">Todos os agentes</option>
                {listaAgentes.map((a) => (
                  <option key={String(a.id)} value={String(a.id)}>{nomeTexto(a.nome)}</option>
                ))}
              </select>
            </div>
            <div className="filter">
              <span className="l">De</span>
              <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
            </div>
            <div className="filter">
              <span className="l">Até</span>
              <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </div>
            <div className="spacer" />
            <div className="actions">
              <button className="btn is-primary" onClick={() => buscar()} disabled={carregando}>
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none"><path d="M1.5 6a4.5 4.5 0 1 0 1.3-3.2M2 1v2.5h2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none" /></svg>
                {carregando ? 'Atualizando…' : 'Atualizar'}
              </button>
            </div>
          </div>

          <div className="caption">
            <span className="l">Mostrando</span>
            <strong>{nomeArea}</strong>
            <span className="pip">/</span>
            <span>Ciclo {ciclo?.texto ?? '—'}</span>
            <span className="pip">·</span>
            <span>{se ? `SE ${se}` : 'todas as SE'}</span>
            <span className="pip">·</span>
            <span>{agenteId ? nomeTexto(listaAgentes.find((a) => String(a.id) === agenteId)?.nome ?? 'agente') : 'todos os agentes'}</span>
            <span className="right">
              <strong>{feitos}</strong> quarteirões · <strong>{porAgente.length}</strong> agentes · <strong>{totalLanc}</strong> lançamentos
            </span>
          </div>

          {erro && <div className="hq-state">⚠️ {erro}</div>}
          {carregando && !dados && <div className="hq-state"><span className="spin" /><div>Carregando o histórico…</div></div>}

          {dados && (
            <>
              {/* Hero progresso */}
              <div className="hero">
                <div className="hero-top">
                  <span className="hero-kicker">Progresso da Área</span>
                  <span className="hero-area">
                    ÁREA <strong>{nomeArea.toUpperCase()}</strong> · CICLO {ciclo?.texto ?? '—'} · <span className="em">EM CAMPO</span>
                  </span>
                </div>
                <div className="hero-row">
                  <div className="hero-pct">{Math.round(progPct)}<span className="u">%</span></div>
                  <div className="hero-bar-col">
                    <div className="hero-title">
                      <span className="em">{feitos} de {totalQ}</span> quarteirões trabalhados <span className="dim">· faltam</span> <span className="em">{faltam}</span>
                    </div>
                    <div className="hero-bar">
                      <div className="fill" style={{ width: `${Math.min(100, progPct)}%` }} />
                      <div className="ticks">{Array.from({ length: 11 }).map((_, i) => <span key={i} />)}</div>
                    </div>
                    <div className="hero-scale"><span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span></div>
                  </div>
                </div>
              </div>

              {/* KPIs */}
              <div className="kpis">
                <div className="kpi">
                  <div className="head">
                    <div className="icon">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <rect x="2" y="2" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" />
                        <rect x="10" y="2" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" fill="currentColor" fillOpacity="0.25" />
                        <rect x="2" y="10" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" fill="currentColor" fillOpacity="0.25" />
                        <rect x="10" y="10" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.4" />
                      </svg>
                    </div>
                    <span className="l">Quarteirões</span>
                  </div>
                  <div className="kpi-body">
                    <div className="kpi-num">{feitos}<span className="of">/</span><span className="total">{totalQ}</span></div>
                    <div className="kpi-side">
                      <div className="row">restam <span className="v">{faltam} quarteirões</span> não trabalhados</div>
                      <div className="kpi-mini"><span style={{ width: `${Math.min(100, progPct)}%` }} /></div>
                      <div className="kpi-meta">
                        <span>Último · Quart. {ultimos[0]?.quarteirao ?? ultimos[0]?.numero ?? trabalhados[trabalhados.length - 1] ? numQ(trabalhados[trabalhados.length - 1]) : '—'}</span>
                        <span>{fmtData(ultData)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="kpi is-ochre">
                  <div className="head">
                    <div className="icon">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M3 8L9 3l6 5v7h-4v-4H7vOH3V8z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span className="l">Imóveis</span>
                  </div>
                  <div className="kpi-body">
                    <div className="kpi-num">{imovFeitos.toLocaleString('pt-BR')}<span className="of">/</span><span className="total">{imovTotal.toLocaleString('pt-BR')}</span></div>
                    <div className="kpi-side">
                      <div className="row">cobertura de <span className="v">{imovPct.toLocaleString('pt-BR')}%</span> dos imóveis da área</div>
                      <div className="kpi-mini"><span style={{ width: `${Math.min(100, imovPct)}%` }} /></div>
                      <div className="kpi-meta"><span>{imovFaltam.toLocaleString('pt-BR')} a vistoriar</span><span>est. via cadastro</span></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mapa */}
              <div className="section-label">
                <span className="bar" />
                <span className="label">Mapa de Quarteirões</span>
                <span className="aside"><strong>{totalQ}</strong> células · <strong>{feitos}</strong> trabalhadas · <strong>{faltam}</strong> a trabalhar</span>
              </div>

              <div className="map-layout">
                <div className="map">
                  <div className="map-head">
                    <div className="title">Quadro de cobertura <span className="area-pill">{nomeArea.toUpperCase()}</span></div>
                    <div className="legend">
                      <span className="item"><span className="chip is-done" /> Trabalhado</span>
                      <span className="item"><span className="chip is-todo" /> A trabalhar</span>
                      <span className="hint">passe o mouse</span>
                    </div>
                  </div>

                  <div className="grid-wrap">
                    <div className="grid">
                      {trabalhados.map((q, i) => (
                        <div
                          key={`d-${i}`}
                          className={`cell is-done${numQ(q).includes('.') ? ' is-sub' : ''}`}
                          onMouseEnter={(e) => mostrarPop(q, e)}
                          onMouseLeave={esconderPop}
                        >
                          {numQ(q)}
                        </div>
                      ))}
                      {Array.from({ length: Math.max(0, faltam) }).map((_, i) => (
                        <div key={`t-${i}`} className="cell is-todo" />
                      ))}
                    </div>
                  </div>

                  <div className="map-foot">
                    <span>Por agente na área</span>
                    <div className="agents">
                      {porAgente.map((a, i) => (
                        <div className="a" key={i}>
                          <span className="av">{iniciais(a.agente ?? a.nome)}</span>
                          <span className="name">{nomeTexto(a.agente ?? a.nome)}</span>
                          <span className="ct">{a.quarteiroes ?? a.total ?? 0}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Feed lateral */}
                <div className="feed">
                  <div className="feed-head">
                    <span className="t">Últimos trabalhados</span>
                    <span className="ct">{ultimos.length} mais recentes</span>
                  </div>
                  <div className="feed-list">
                    {ultimos.map((u, i) => (
                      <div className={`feed-item${i >= 4 ? ' is-old' : ''}`} key={i}>
                        <div className="av">{iniciais(u.agente ?? u.nome)}</div>
                        <div className="body">
                          <span className="num"><span className="label">Quart.</span>{u.quarteirao ?? u.numero ?? '—'}</span>
                          <span className="agent">{nomeTexto(u.agente ?? u.nome)}</span>
                        </div>
                        <div className="when"><span className="d">{diaMes(u.data)}</span>{u.hora ?? ''}</div>
                      </div>
                    ))}
                    {ultimos.length === 0 && <div className="hq-state" style={{ padding: '24px 0' }}>Sem lançamentos ainda.</div>}
                  </div>
                  <div className="feed-foot"><a>Ver histórico completo →</a></div>
                </div>
              </div>

              <div className="doc-foot">
                <span>SISTEMA TOVIS · <span className="em">MÓDULO HISTÓRICO QUARTEIRÃO</span></span>
                <span>FONTE FAD-01 · {totalLanc} LANÇAMENTOS</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Popover flutuante */}
      <div className="float-pop" ref={popRef}>
        <div className="pop-kicker">Quarteirão</div>
        <div className="pop-num fp-num">—</div>
        <div className="pop-row"><span className="l">Agente</span><span className="v fp-agent">—</span></div>
        <div className="pop-row"><span className="l">Data</span><span className="v mono fp-date">—</span></div>
        <div className="pop-row"><span className="l">Imóveis</span><span className="v mono fp-imov">—</span></div>
        <div className="pop-row"><span className="l">SE</span><span className="v mono fp-se">—</span></div>
      </div>
    </div>
  );
}
